/**
 * YourSubmissionsBell — the header "your submissions" trigger + panel shell.
 * SPEC-stakeholder-portal-presentation-redesign-2026-07-20, R2 (trigger) + R3
 * (the tabbed panel shell it now opens).
 *
 * Retires the two Stakeholder Portal / Coordination left-nav pages (R1 AC1)
 * in favor of a small, ICON-ONLY affordance mounted next to Report Feedback
 * (ReportFeedbackButton) — notification-bell scale, no visible text label,
 * distinct in visual weight from the labeled Report Feedback button. Mirrors
 * the existing GlobalMessagesBell / NotificationBell icon+badge+popover+
 * polling idiom for the trigger/badge mechanics.
 *
 * R3 AC1/AC2: the panel SCALES by role from the SAME trigger — a `stakeholder
 * _portal`-capable client gets a light Popover (My Submissions + Roadmap); a
 * super-admin gets a fuller Sheet drawer (adds Coordination + All Issues).
 * Open/active-tab state is READ from PortalPanelContext (not local state) so
 * `ReportFeedbackButton`'s post-submit receipt flow and `YourIssuesPage`'s
 * portal-enabled redirect can open this panel from elsewhere in the app
 * (R3 AC6 — land on the receipt via the open panel, never a dead route).
 *
 * Opening the panel marks the caller's submissions seen (R2 AC4): the badge
 * does NOT clear until the write is server-CONFIRMED (see
 * useMarkPortalSeen's onSuccess) — a write failure leaves the badge exactly
 * as it was, never a silent desync.
 */
import { Inbox } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { useAuth } from '@/lib/auth/AuthProvider';
import { UserRole } from '@/types/auth.ts';
import { usePortalUnseenCount, useMarkPortalSeen } from '@/hooks/usePortalUnseen';
import { usePortalPanel } from '@/lib/portal/PortalPanelContext';
import { PanelBody } from '@/components/portal/panel/PanelBody';

// The per-user capability that gates the portal (mirrors ReportFeedbackButton
// + SideNav's retired stakeholder-portal gate).
const STAKEHOLDER_PORTAL_CAP = 'stakeholder_portal';

export function YourSubmissionsBell() {
  const { user, can } = useAuth();
  const { isOpen, setOpen } = usePortalPanel();

  // AC2: visible to a stakeholder_portal-capable user, and to super-admins.
  // `can` already treats a tenant admin as fully permissioned (including the
  // typical super-admin default role), but is_super_admin is checked
  // explicitly too so this can never silently depend on that role mapping.
  const isSuperAdmin = !!user?.is_super_admin;
  const visible =
    user?.role !== UserRole.EXTERNAL &&
    ((typeof can === 'function' && can(STAKEHOLDER_PORTAL_CAP)) || isSuperAdmin);
  // v0.17.15: every My Submissions row is the caller's OWN report (GET
  // /api/stakeholder-portal/issues is reporter-scoped), and the backend lets a
  // reporter Confirm / Undo / Still broken on her own report with or without
  // the resolve_issues grant. So the actions apply to every row shown here.
  // The old `can('resolve_issues')` was wrong both ways: it let any admin
  // through on the frontend (can() bypasses on is_admin) while the backend's
  // fail-closed gate 403'd that same admin.
  const canResolve = true;

  // Hooks must run unconditionally (Rules of Hooks) — the `enabled` gate is
  // what actually stops the request for an ineligible user.
  const { data: unseenCount = 0 } = usePortalUnseenCount(visible);
  const markSeen = useMarkPortalSeen();

  if (!visible) return null;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    // R2 AC4: opening the panel marks the caller's submissions seen. Only
    // fire the write when there is something to acknowledge and no write is
    // already in flight (avoids a redundant call on a rapid re-open).
    if (next && unseenCount > 0 && !markSeen.isPending) {
      markSeen.mutate();
    }
  };

  const trigger = (
    <Button
      variant="ghost"
      size="icon"
      className="relative"
      aria-label={`Your submissions${unseenCount > 0 ? ` (${unseenCount} updated)` : ''}`}
    >
      <Inbox className="h-4 w-4" />
      {unseenCount > 0 && (
        <Badge
          variant="destructive"
          className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
          data-testid="your-submissions-badge"
        >
          {unseenCount > 9 ? '9+' : unseenCount}
        </Badge>
      )}
    </Button>
  );

  // R3 AC2: the fuller drawer variant for a super-admin (built on sheet.tsx).
  if (isSuperAdmin) {
    return (
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        {/* v0.17.2: sm:max-w-md is 448px and the super-admin table measures
            ~454px, so the Answer button rendered as "An" and 48px sat behind two
            stacked horizontal scrollbars. Trimming a column first was the wrong
            fix -- table-layout:auto let the content override w-[180px], so
            220 -> 180 rendered at 197 and changed nothing. The container is the
            constraint, so widen it. Client users are unaffected either way: they
            get the stacked-card Popover below, never this table. */}
        <SheetContent
          side="right"
          className="w-full p-0 sm:max-w-2xl flex flex-col"
          data-testid="portal-panel-drawer"
        >
          <SheetTitle className="sr-only">Your Submissions</SheetTitle>
          <SheetDescription className="sr-only">
            Your submissions, the roadmap, and (for super-admins) coordination and all issues.
          </SheetDescription>
          <div className="px-3 py-2 border-b">
            <span className="font-semibold text-sm">Your Submissions</span>
          </div>
          <PanelBody canResolve={canResolve} variant="drawer" />
        </SheetContent>
      </Sheet>
    );
  }

  // R3 AC1: the light popover variant for a plain client.
  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        // LOW visual-review finding (2026-07-24 final pass): cap to the
        // viewport so a narrow/mobile screen never clips or overflows.
        className="w-96 max-w-[calc(100vw-2rem)] p-0"
        data-testid="portal-panel-popover"
      >
        <div className="px-3 py-2 border-b">
          <span className="font-semibold text-sm">Your Submissions</span>
        </div>
        <PanelBody canResolve={canResolve} variant="popover" />
      </PopoverContent>
    </Popover>
  );
}
